// Interface. Les données brutes de chaque niveau (résultats, saisons,
// épisodes) sont gardées telles que la source les a rendues ; le filtre de
// langue s'applique au rendu, ce qui permet de basculer VF/VOSTFR sans rien
// recharger.

import * as aniskip from "./aniskip.ts";
import type { SkipInterval } from "./aniskip.ts";
import { loadConfig } from "./constants.ts";
import type { Source } from "./constants.ts";
import { download, save as saveFile } from "./download.ts";
import type { Progress } from "./download.ts";
import { pickVariant } from "./hls.ts";
import * as history from "./history.ts";
import type { Watched } from "./history.ts";
import {
  episodeLang,
  filterLang,
  isLang,
  langLabel,
  seasonLang,
  titleLang,
  withLang,
} from "./lang.ts";
import type { Lang } from "./lang.ts";
import { Player, parseQuality, serializeQuality } from "./player.ts";
import type { Choice, Quality } from "./player.ts";
import { parsePreload } from "./preload.ts";
import { embedLabel } from "./resolvers.ts";
import type { Preload, WarmProgress } from "./preload.ts";
import * as animesama from "./sources/animesama.ts";
import type { AnimeHit, Season } from "./sources/animesama.ts";
import * as voiranime from "./sources/voiranime.ts";
import { firstPlayableStream, probeEmbeds } from "./stream.ts";
import type { Probe, Stream } from "./stream.ts";
import * as tv from "./tv.ts";

interface Episode {
  label: string;
  /** Index d'origine : dans les candidats anime-sama, ou dans la liste
   *  d'épisodes voir-anime avant filtrage. */
  index: number;
  /** voir-anime uniquement : URL de la page épisode. */
  url?: string;
}

type View = "home" | "results" | "seasons" | "episodes" | "player";

const el = <T extends HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

const ui = {
  home: el<HTMLButtonElement>("home"),
  form: el<HTMLFormElement>("search"),
  query: el<HTMLInputElement>("query"),
  source: el<HTMLSelectElement>("source"),
  lang: el<HTMLSelectElement>("lang"),
  tv: el<HTMLButtonElement>("tv"),
  theme: el<HTMLButtonElement>("theme"),
  crumbs: el<HTMLElement>("crumbs"),
  status: el<HTMLParagraphElement>("status"),
  listView: el<HTMLElement>("list-view"),
  listTitle: el<HTMLHeadingElement>("list-title"),
  list: el<HTMLElement>("list"),
  playerView: el<HTMLElement>("player-view"),
  playerTitle: el<HTMLHeadingElement>("player-title"),
  playerPosition: el<HTMLElement>("player-position"),
  playerSource: el<HTMLElement>("player-source"),
  playerMessage: el<HTMLElement>("player-message"),
  video: el<HTMLVideoElement>("video"),
  settings: el<HTMLButtonElement>("settings"),
  settingsPanel: el<HTMLElement>("settings-panel"),
  quality: el<HTMLSelectElement>("quality"),
  qualityField: el<HTMLElement>("quality-field"),
  qualityState: el<HTMLElement>("quality-state"),
  preload: el<HTMLSelectElement>("preload"),
  preloadState: el<HTMLElement>("preload-state"),
  prev: el<HTMLButtonElement>("prev"),
  next: el<HTMLButtonElement>("next"),
  skip: el<HTMLButtonElement>("skip"),
  autoskip: el<HTMLInputElement>("autoskip"),
  downloadBtn: el<HTMLButtonElement>("download"),
  downloadState: el<HTMLElement>("download-state"),
  downloadProgress: el<HTMLProgressElement>("download-progress"),
  downloadLabel: el<HTMLElement>("download-label"),
  downloadCancel: el<HTMLButtonElement>("download-cancel"),
  devPanel: el<HTMLElement>("dev-panel"),
  devClose: el<HTMLButtonElement>("dev-close"),
  devStream: el<HTMLElement>("dev-stream"),
  devTest: el<HTMLButtonElement>("dev-test"),
  devPlayers: el<HTMLElement>("dev-players"),
  devServer: el<HTMLElement>("dev-server"),
  devFailures: el<HTMLElement>("dev-failures"),
};

const state = {
  source: "animesama" as Source,
  lang: "vostfr" as Lang,
  view: "home" as View,
  hits: [] as AnimeHit[],
  anime: null as AnimeHit | null,
  allSeasons: [] as Season[],
  season: null as Season | null,
  /** Chemin réellement chargé, langue comprise : il peut différer de celui du
   *  panneau, qui n'annonce que le VOSTFR. */
  seasonPath: "",
  /** voir-anime : épisodes non filtrés. */
  allEpisodes: [] as Episode[],
  /** Liste effectivement affichée, après filtre de langue. */
  episodes: [] as Episode[],
  /** anime-sama : lecteurs par index d'épisode, mis en cache pour la saison. */
  candidates: [] as string[][],
  /** Position dans `episodes`, -1 hors lecture. */
  current: -1,
  stream: null as Stream | null,
  historyKey: "" as string,
  /** Intervalles d'intro et de fin de l'épisode en cours. */
  skips: [] as SkipInterval[],
};

/** Intervalle sous le curseur, celui que le bouton ferait passer. */
let activeSkip: SkipInterval | null = null;
/** Intervalles déjà passés automatiquement : sans cette mémoire, revenir en
 *  arrière pour revoir l'intro relancerait le saut en boucle. */
const autoSkipped = new Set<string>();

let downloadAbort: AbortController | null = null;
let searchAbort: AbortController | null = null;
let searchTimer = 0;
/** Numéro de la dernière recherche lancée : les réponses en retard sont
 *  ignorées plutôt que d'écraser un résultat plus récent. */
let searchGeneration = 0;
let pendingSeek = 0;
let lastSaved = 0;

/** Fréquence d'enregistrement de la position de lecture, en millisecondes. */
const SAVE_INTERVAL = 1000;

const player = new Player(
  ui.video,
  showChoices,
  playbackError,
  showPreload,
);

/** Avancement du préchargement, à côté du nom du lecteur. */
function showPreload(progress: WarmProgress | null): void {
  if (!progress) {
    ui.preloadState.textContent = "";
    return;
  }

  ui.preloadState.textContent =
    progress.done >= progress.total
      ? "épisode préchargé"
      : `préchargé ${progress.done} / ${progress.total}`;
}

/** Clé du mode de préchargement, retenu comme la qualité. */
const PRELOAD_KEY = "aniweb-preload";

function storedPreload(): Preload {
  try {
    return parsePreload(localStorage.getItem(PRELOAD_KEY));
  } catch {
    return "normal";
  }
}

/** Clé du choix de qualité : il vaut pour tous les épisodes, pas pour un seul. */
const QUALITY_KEY = "aniweb-quality";

function rememberQuality(quality: Quality): void {
  try {
    localStorage.setItem(QUALITY_KEY, serializeQuality(quality));
  } catch {
    // stockage indisponible : le choix vaudra pour cette session seulement
  }
}

function storedQuality(): Quality {
  try {
    return parseQuality(localStorage.getItem(QUALITY_KEY));
  } catch {
    return "auto";
  }
}

// --- affichage ---------------------------------------------------------------

function setStatus(message: string, isError = false): void {
  ui.status.textContent = message;
  ui.status.hidden = message === "";
  ui.status.classList.toggle("error", isError);

  // En plein écran, seul le conteneur du lecteur reste visible : le message y
  // est doublé, la feuille de style ne l'affiche que dans ce cas.
  ui.playerMessage.textContent = message;
  ui.playerMessage.hidden = message === "" || state.view !== "player";
  ui.playerMessage.classList.toggle("error", isError);
}

/** Un échec de lecture ne doit pas passer inaperçu. En plein écran — celui où
 *  l'épisode suivant s'enchaîne seul — la barre d'état est hors de vue, tout
 *  comme Précédent et Suivant : on en sort pour rendre les deux. */
function playbackError(message: string): void {
  setStatus(message, true);
  exitFullscreen();
}

interface Row {
  label: string;
  sub?: string;
  onClick: () => void;
  onRemove?: () => void;
}

function renderRows(rows: Row[], layout: "rows" | "grid", empty: string): void {
  if (rows.length === 0) {
    const message = document.createElement("p");
    message.className = "empty";
    message.textContent = empty;
    ui.list.replaceChildren(message);

    return;
  }

  const container = document.createElement("div");
  container.className = layout;

  for (const row of rows) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "entry";
    button.addEventListener("click", row.onClick);

    const label = document.createElement("span");
    label.className = "label";
    label.textContent = row.label;
    button.append(label);

    if (row.sub) {
      const sub = document.createElement("span");
      sub.className = "sub";
      sub.textContent = row.sub;
      button.append(sub);
    }

    if (!row.onRemove) {
      container.append(button);
      continue;
    }

    // le bouton de suppression ne peut pas être imbriqué dans le premier
    const group = document.createElement("div");
    group.className = "row-group";

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "remove";
    remove.title = "Retirer de l'historique";
    remove.setAttribute("aria-label", `Retirer ${row.label} de l'historique`);
    remove.textContent = "✕";
    remove.addEventListener("click", row.onRemove);

    group.append(button, remove);
    container.append(group);
  }

  ui.list.replaceChildren(container);
}

function crumb(label: string, onClick?: () => void): HTMLElement {
  if (!onClick) {
    const span = document.createElement("span");
    span.className = "current";
    span.textContent = label;

    return span;
  }

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.addEventListener("click", onClick);

  return button;
}

function renderCrumbs(): void {
  const parts: HTMLElement[] = [];

  if (state.view !== "home") {
    parts.push(crumb("Accueil", () => showHome()));
  }

  if (state.hits.length > 0 && state.view !== "home") {
    parts.push(crumb("Résultats", state.anime ? () => showResults() : undefined));
  }

  if (state.anime) {
    const deeper = state.view === "episodes" || state.view === "player";

    parts.push(
      crumb(
        state.anime.name,
        deeper || state.view === "seasons" ? () => void openAnime(state.anime!) : undefined,
      ),
    );
  }

  if (state.season) {
    parts.push(
      crumb(state.season.name, state.view === "player" ? () => showEpisodes() : undefined),
    );
  }

  if (state.view === "player" && state.current >= 0) {
    parts.push(crumb(`Épisode ${state.episodes[state.current]?.label ?? ""}`));
  }

  ui.crumbs.replaceChildren(
    ...parts.flatMap((part, i) => (i === 0 ? [part] : [separator(), part])),
  );

  ui.crumbs.hidden = parts.length === 0;
}

function separator(): HTMLElement {
  const span = document.createElement("span");
  span.textContent = "›";

  return span;
}

function showListView(title: string): void {
  ui.listView.hidden = false;
  ui.playerView.hidden = true;
  ui.listTitle.textContent = title;
  ui.listTitle.hidden = title === "";

  hideSkip();
  player.destroy();
}

// --- vues --------------------------------------------------------------------

function render(): void {
  // un message vaut pour l'écran qui l'a posé, pas pour le suivant ; les vues
  // qui ont quelque chose à dire le font depuis leur propre rendu
  setStatus("");
  renderCrumbs();

  switch (state.view) {
    case "home":
      renderHome();
      break;
    case "results":
      renderResults();
      break;
    case "seasons":
      renderSeasons();
      break;
    case "episodes":
      renderEpisodes();
      break;
    case "player":
      break;
  }
}

function renderHome(): void {
  showListView("Reprendre");

  const entries = history.list();

  renderRows(
    entries.map((entry) => ({
      label: entry.animeName,
      sub: history.describe(entry),
      onClick: () => void resume(entry),
      onRemove: () => {
        history.remove(entry.key);
        renderHome();
      },
    })),
    "rows",
    "Rien encore. Lance une recherche pour commencer.",
  );
}

function visibleHits(): AnimeHit[] {
  // chez voir-anime la langue est portée par le titre de la fiche ;
  // chez anime-sama elle ne se joue qu'au niveau des saisons
  if (state.source !== "voiranime") return state.hits;

  return filterLang(state.hits, state.lang, (hit) => titleLang(hit.name)).items;
}

function renderResults(): void {
  showListView("");

  const hits = visibleHits();

  renderRows(
    hits.map((hit) => ({ label: hit.name, onClick: () => void openAnime(hit) })),
    "rows",
    "Aucun résultat.",
  );
}

function renderSeasons(): void {
  showListView(state.anime?.name ?? "");

  // Aucun filtre de langue ici : anime-sama n'annonce que le VOSTFR dans ses
  // panneaux. La langue se joue à l'ouverture de la saison, en visant l'URL
  // correspondante.
  renderRows(
    state.allSeasons.map((season) => ({
      label: season.name,
      onClick: () => void openSeason(season),
    })),
    "rows",
    "Aucune saison trouvée.",
  );
}

/** Applique le filtre de langue aux épisodes et met à jour `state.episodes`,
 *  dont dépendent les positions utilisées partout ailleurs. */
function refreshEpisodes(): void {
  if (state.source !== "voiranime") {
    state.episodes = state.allEpisodes;
    return;
  }

  state.episodes = filterLang(state.allEpisodes, state.lang, (episode) =>
    episodeLang(episode.label),
  ).items;
}

function renderEpisodes(): void {
  refreshEpisodes();
  showListView(state.season?.name ?? state.anime?.name ?? "");

  renderRows(
    state.episodes.map((episode, position) => ({
      label: episode.label,
      onClick: () => void play(position),
    })),
    state.source === "voiranime" ? "rows" : "grid",
    "Aucun épisode disponible.",
  );
}

function showHome(): void {
  state.view = "home";
  state.anime = null;
  state.season = null;
  state.current = -1;

  render();
}

function showResults(): void {
  state.view = "results";
  state.anime = null;
  state.season = null;
  state.allSeasons = [];
  state.current = -1;

  render();
}

function showEpisodes(): void {
  state.view = "episodes";
  state.current = -1;

  render();
}

// --- recherche ---------------------------------------------------------------

function scheduleSearch(): void {
  clearTimeout(searchTimer);

  const query = ui.query.value.trim();

  if (query.length < 2) {
    searchAbort?.abort();
    setStatus("");

    if (state.view !== "home") showHome();

    return;
  }

  searchTimer = window.setTimeout(() => void runSearch(query), 300);
}

async function runSearch(query: string): Promise<void> {
  searchAbort?.abort();
  searchAbort = new AbortController();

  const generation = ++searchGeneration;
  const { signal } = searchAbort;

  setStatus("Recherche…");

  const hits =
    state.source === "voiranime"
      ? await voiranime.search(query, signal)
      : await animesama.search(query, signal);

  // une frappe plus récente a pris la main
  if (generation !== searchGeneration) return;

  state.hits = hits;
  state.view = "results";

  render();

  const shown = visibleHits().length;

  setStatus(
    shown === 0
      ? hits.length === 0
        ? "Aucun résultat."
        : `Aucun résultat en ${langLabel(state.lang)}.`
      : "",
  );
}

// --- navigation --------------------------------------------------------------

async function openAnime(anime: AnimeHit): Promise<void> {
  state.anime = anime;
  state.season = null;
  state.current = -1;

  // Une fiche neuve n'hérite pas de la saison précédente. Sans cette remise à
  // zéro, le chemin laissé par une saison anime-sama se retrouvait dans la clé
  // et l'entrée d'historique d'une fiche voir-anime, qui n'a pas de saison :
  // reprendre depuis l'accueil repartait alors chercher cette saison chez
  // anime-sama et ne trouvait plus aucun épisode.
  state.seasonPath = "";
  state.candidates = [];

  setStatus("Chargement…");

  // voir-anime n'a pas de niveau saison : une fiche = une liste d'épisodes
  if (state.source === "voiranime") {
    const episodes = await voiranime.episodes(anime.url);

    state.allSeasons = [];
    state.allEpisodes = episodes.map((episode, index) => ({
      label: episode.name,
      index,
      url: episode.url,
    }));

    state.view = "episodes";
    setStatus("");
    render();

    return;
  }

  state.allSeasons = await animesama.seasons(anime.url);
  state.view = "seasons";

  setStatus("");
  render();
}

interface SeasonLoad {
  /** Des épisodes ont été trouvés. */
  loaded: boolean;
  /** La langue demandée n'existait pas : on est revenu à celle du panneau. */
  fellBack: boolean;
}

/** `exactPath` sert à rouvrir une entrée d'historique telle qu'elle avait été
 *  lue, sans redevinner la langue. */
async function openSeason(season: Season, exactPath?: string): Promise<SeasonLoad> {
  state.season = season;
  state.current = -1;

  setStatus("Chargement des épisodes…");

  const wanted = exactPath ?? withLang(season.path, state.lang);

  let candidates = await animesama.episodes(state.anime!.url, wanted);
  let used = wanted;
  let fellBack = false;

  // la langue demandée n'existe pas pour cette saison : la page 404 servie à sa
  // place ne contient aucun épisode, on revient donc à celle du panneau
  if (candidates.length === 0 && wanted !== season.path) {
    candidates = await animesama.episodes(state.anime!.url, season.path);
    used = season.path;
    fellBack = candidates.length > 0;
  }

  state.seasonPath = used;
  state.candidates = candidates;

  state.allEpisodes = candidates.map((_, index) => ({
    label: String(index + 1),
    index,
  }));

  state.view = "episodes";
  render();

  if (fellBack) {
    setStatus(
      `Pas de ${langLabel(state.lang)} pour cette saison : ` +
        `${langLabel(seasonLang(used) ?? "vostfr")} affiché.`,
    );
  }

  return { loaded: candidates.length > 0, fellBack };
}

// --- lecture -----------------------------------------------------------------

function episodeTitle(position: number): string {
  const base = state.season?.name ?? state.anime?.name ?? "";

  return `${base} - Épisode ${state.episodes[position]?.label ?? ""}`;
}

/** Lecteurs proposés pour un épisode. anime-sama les a donnés d'un bloc avec la
 *  saison ; voir-anime demande une requête par épisode. */
async function embedsFor(position: number): Promise<string[]> {
  const episode = state.episodes[position];
  if (!episode) return [];

  if (state.source === "voiranime") return await voiranime.embeds(episode.url!);

  return state.candidates[episode.index] ?? [];
}

async function play(position: number): Promise<void> {
  const episode = state.episodes[position];
  if (!episode) return;

  state.current = position;
  state.stream = null;
  state.view = "player";
  state.skips = [];

  hideSkip();

  // L'épisode précédent est coupé tout de suite. Sinon, faute de flux pour
  // celui-ci, l'ancien restait chargé dans le lecteur et pouvait repartir.
  player.destroy();

  // les sondes valent pour un épisode : les garder afficherait, au suivant, un
  // verdict qui ne le concerne pas
  ui.devPlayers.replaceChildren();

  ui.listView.hidden = true;
  ui.playerView.hidden = false;
  ui.playerTitle.textContent = episodeTitle(position);
  ui.playerSource.textContent = "";
  ui.qualityField.hidden = true;
  ui.qualityState.textContent = "";
  ui.preloadState.textContent = "";
  ui.downloadState.hidden = true;
  ui.prev.disabled = position === 0;
  ui.next.disabled = position === state.episodes.length - 1;

  showPosition(position);
  setDrawer(false);

  renderCrumbs();
  prepareResume(position);

  setStatus("Recherche d'un lecteur…");

  const embeds = await embedsFor(position);

  if (embeds.length === 0) {
    playbackError("Aucun lecteur compatible trouvé pour cet épisode.");
    return;
  }

  // on bascule d'un lecteur à l'autre tant qu'aucun ne rend de flux jouable
  const stream = await firstPlayableStream(embeds, setStatus);

  // l'utilisateur a pu changer d'épisode entre-temps
  if (state.current !== position || state.view !== "player") return;

  if (!stream) {
    playbackError("Impossible de récupérer le flux vidéo.");
    return;
  }

  state.stream = stream;

  setStatus("");
  ui.playerSource.textContent = `via ${stream.player}`;

  rememberEpisode(position);
  player.load(stream);
}

function showChoices(choices: Choice[], current: string): void {
  ui.qualityField.hidden = choices.length === 0;

  if (choices.length === 0) {
    ui.qualityState.textContent = "";
    return;
  }

  ui.quality.replaceChildren(
    ...choices.map((choice) => {
      const option = document.createElement("option");
      option.value = choice.value;
      option.textContent = choice.label;

      return option;
    }),
  );

  ui.quality.value = current;
  showQuality();
}

/** La qualité vit dans le tiroir : sans rappel, on ne saurait plus laquelle
 *  est active sans l'ouvrir. */
function showQuality(): void {
  ui.qualityState.textContent = ui.quality.selectedOptions[0]?.textContent ?? "";
}

/** Le compteur qui remplace les libellés « Précédent » et « Suivant ». */
function showPosition(position: number): void {
  const label = state.episodes[position]?.label ?? "";
  const current = document.createElement("b");

  current.textContent = `Épisode ${label}`;
  ui.playerPosition.replaceChildren(current, ` sur ${state.episodes.length}`);
}

// --- tiroir de réglages ------------------------------------------------------

function setDrawer(open: boolean): void {
  ui.settingsPanel.hidden = !open;
  ui.settings.setAttribute("aria-expanded", String(open));
}

// --- historique --------------------------------------------------------------

/** Retrouve où l'on s'était arrêté, avant même de savoir si le flux existe. */
function prepareResume(position: number): void {
  if (!state.anime) return;

  // la clé porte le chemin réellement chargé : VF et VOSTFR d'une même saison
  // sont deux suivis distincts
  state.historyKey = history.entryKey(
    state.source,
    state.anime.url,
    state.seasonPath || null,
  );

  lastSaved = 0;

  const existing = history.find(state.historyKey);

  pendingSeek =
    existing && existing.episodePos === position ? history.resumeAt(existing) : 0;
}

/** N'inscrit l'épisode qu'une fois un flux obtenu : un épisode illisible n'a
 *  rien à faire dans « Reprendre ». */
function rememberEpisode(position: number): void {
  if (!state.anime) return;

  const episode = state.episodes[position];
  if (!episode) return;

  const key = state.historyKey;
  const existing = history.find(key);
  const samePlace = existing?.episodePos === position;

  history.save({
    key,
    source: state.source,
    lang: state.lang,
    animeName: state.anime.name,
    animeUrl: state.anime.url,
    seasonName: state.season?.name ?? null,
    seasonPath: state.seasonPath || null,
    episodePos: position,
    episodeLabel: episode.label,
    position: samePlace ? (existing?.position ?? 0) : 0,
    duration: samePlace ? (existing?.duration ?? 0) : 0,
    at: Date.now(),
  });
}

/** Rejoue une entrée : on refait le chemin complet pour que la liste
 *  d'épisodes, et donc Précédent/Suivant, soient de nouveau disponibles. */
async function resume(entry: Watched): Promise<void> {
  state.source = entry.source;
  state.lang = entry.lang;
  ui.source.value = entry.source;
  ui.lang.value = entry.lang;

  const anime: AnimeHit = { name: entry.animeName, url: entry.animeUrl };

  await openAnime(anime);

  // voir-anime n'a pas de niveau saison : un chemin y est forcément le reste
  // d'une entrée écrite avant la remise à zéro d'`openAnime`, et l'ouvrir
  // viderait la liste d'épisodes qu'on vient de charger
  if (entry.seasonPath && entry.source !== "voiranime") {
    const season =
      state.allSeasons.find((other) => other.path === entry.seasonPath) ??
      ({ name: entry.seasonName ?? entry.animeName, path: entry.seasonPath } as Season);

    // le chemin est repris tel quel : il porte déjà la langue écoutée
    await openSeason(season, entry.seasonPath);
  }

  refreshEpisodes();

  if (state.episodes.length === 0) {
    setStatus("Cet épisode n'est plus disponible.", true);
    return;
  }

  await play(Math.min(entry.episodePos, state.episodes.length - 1));
}

ui.video.addEventListener("loadedmetadata", () => {
  if (pendingSeek > 0 && pendingSeek < ui.video.duration) {
    ui.video.currentTime = pendingSeek;
  }

  pendingSeek = 0;

  // la durée n'est connue qu'ici, et AniSkip en a besoin
  void loadSkips(state.current);
});

// --- intro et générique de fin -----------------------------------------------

/** Le numéro d'épisode tel qu'AniSkip le comprend : anime-sama numérote de 1 à
 *  n, voir-anime nomme ses épisodes « 293 VF ». */
function episodeNumber(position: number): number | null {
  const label = state.episodes[position]?.label;
  const digits = label?.match(/\d+/)?.[0];

  return digits === undefined ? null : Number(digits);
}

async function loadSkips(position: number): Promise<void> {
  state.skips = [];
  autoSkipped.clear();
  hideSkip();

  if (!state.anime || position < 0) return;

  const episode = episodeNumber(position);
  const duration = ui.video.duration;

  if (episode === null || !Number.isFinite(duration) || duration <= 0) return;

  const found = await aniskip.lookup({
    // l'identifiant vaut pour la saison, quelle que soit la langue écoutée
    cacheKey: `${state.source}|${state.anime.url}|${state.season?.name ?? ""}`,
    animeName: state.anime.name,
    seasonName: state.season?.name ?? null,
    episode,
    duration,
  });

  // l'utilisateur a pu changer d'épisode pendant la requête
  if (state.current !== position) return;

  state.skips = found;
}

function hideSkip(): void {
  activeSkip = null;
  ui.skip.hidden = true;
}

/** Appelé à chaque `timeupdate` : montre le bouton tant que le curseur est dans
 *  un intervalle connu, et le passe seul si l'option est cochée. */
function updateSkip(): void {
  const time = ui.video.currentTime;

  const current =
    state.skips.find((skip) => time >= skip.start && time < skip.end - 0.5) ?? null;

  if (!current) {
    if (activeSkip) hideSkip();
    return;
  }

  if (ui.autoskip.checked && !autoSkipped.has(current.id)) {
    autoSkipped.add(current.id);
    ui.video.currentTime = current.end;
    hideSkip();

    return;
  }

  if (activeSkip?.id === current.id) return;

  activeSkip = current;
  ui.skip.textContent = aniskip.label(current.type);
  ui.skip.hidden = false;
}

ui.skip.addEventListener("click", () => {
  if (!activeSkip) return;

  ui.video.currentTime = activeSkip.end;
  hideSkip();
});

ui.autoskip.addEventListener("change", () => {
  try {
    localStorage.setItem("aniweb-autoskip", ui.autoskip.checked ? "1" : "0");
  } catch {
    // stockage indisponible : le choix vaudra pour cette session
  }

  if (ui.autoskip.checked) updateSkip();
});

ui.video.addEventListener("timeupdate", () => {
  updateSkip();

  // Durée inconnue : le lecteur vient d'être vidé, ce `timeupdate` est celui
  // de la remise à zéro et écraserait la position mémorisée.
  const { duration } = ui.video;
  if (state.historyKey === "" || !Number.isFinite(duration) || duration <= 0) return;

  // `timeupdate` se déclenche environ quatre fois par seconde ; on n'écrit
  // qu'une fois par seconde
  const now = Date.now();
  if (now - lastSaved < SAVE_INTERVAL) return;

  lastSaved = now;
  history.progress(state.historyKey, ui.video.currentTime, ui.video.duration);
});

// --- langue ------------------------------------------------------------------

async function switchLang(lang: Lang): Promise<void> {
  state.lang = lang;
  setStatus("");

  const position = state.current;
  const wasPlaying = state.view === "player";

  // anime-sama : la langue est une autre URL de saison, il faut la recharger —
  // aussi bien depuis la liste d'épisodes que pendant la lecture
  if (
    state.source !== "voiranime" &&
    state.season &&
    (state.view === "episodes" || wasPlaying)
  ) {
    const result = await openSeason(state.season);

    // repli ou saison vide : le message est déjà posé, on reste sur la liste
    if (!result.loaded || result.fellBack) return;

    if (wasPlaying && position >= 0 && position < state.episodes.length) {
      await play(position);
    }

    return;
  }

  if (!wasPlaying) {
    render();
    return;
  }

  // voir-anime : retrouver le même numéro dans la liste de l'autre langue
  const wanted = state.episodes[position]?.label.match(/\d+/)?.[0];

  refreshEpisodes();

  const twin = state.episodes.findIndex(
    (episode) => episode.label.match(/\d+/)?.[0] === wanted,
  );

  if (twin === -1) {
    showEpisodes();
    setStatus(`Cet épisode n'est pas disponible en ${langLabel(lang)}.`);

    return;
  }

  await play(twin);
}

// --- téléchargement ----------------------------------------------------------

function downloadTarget(stream: Stream): { url: string; isHLS: boolean } {
  if (!stream.isHLS || !stream.manifest) {
    return { url: stream.url, isHLS: stream.isHLS };
  }

  // le téléchargement travaille sur une playlist de segments concrète : on prend
  // la variante correspondant au choix de qualité, la meilleure sinon
  const variants = stream.manifest.variants;

  if (variants.length === 0) return { url: stream.url, isHLS: true };

  const quality = parseQuality(ui.quality.value);

  // en Auto, la hauteur servie varie au fil du débit : on télécharge la
  // meilleure plutôt qu'un palier arbitraire
  if (quality === "auto" || quality === "best") {
    return { url: pickVariant(variants, "best")?.url ?? stream.url, isHLS: true };
  }

  let wanted = variants[0]!;
  let gap = Infinity;

  for (const variant of variants) {
    const distance = Math.abs(variant.height - quality);

    if (distance < gap) {
      gap = distance;
      wanted = variant;
    }
  }

  return { url: wanted.url, isHLS: true };
}

function showProgress(progress: Progress): void {
  const mo = (progress.bytes / 1_048_576).toFixed(1);

  if (progress.total > 0) {
    ui.downloadProgress.max = progress.total;
    ui.downloadProgress.value = progress.done;
  }

  ui.downloadLabel.textContent =
    progress.total > 0 && progress.done <= progress.total
      ? `${progress.done} / ${progress.total} — ${mo} Mo`
      : `${mo} Mo`;
}

async function startDownload(): Promise<void> {
  if (!state.stream || downloadAbort) return;

  const target = downloadTarget(state.stream);

  downloadAbort = new AbortController();

  ui.downloadState.hidden = false;
  ui.downloadBtn.disabled = true;
  ui.downloadProgress.value = 0;
  ui.downloadLabel.textContent = "Démarrage…";

  try {
    const result = await download({
      url: target.url,
      isHLS: target.isHLS,
      name: episodeTitle(state.current),
      onProgress: showProgress,
      signal: downloadAbort.signal,
    });

    saveFile(result);
    ui.downloadLabel.textContent = `${result.filename} — terminé`;
  } catch (err) {
    if (downloadAbort.signal.aborted) {
      ui.downloadLabel.textContent = "Annulé.";
    } else {
      setStatus(`Téléchargement impossible : ${(err as Error).message}`, true);
      ui.downloadState.hidden = true;
    }
  } finally {
    downloadAbort = null;
    ui.downloadBtn.disabled = false;
  }
}

// --- retour arrière ----------------------------------------------------------

function goBack(): void {
  // le tiroir ouvert est ce qu'on veut fermer en premier
  if (!ui.settingsPanel.hidden) {
    setDrawer(false);
    return;
  }

  switch (state.view) {
    case "player":
      showEpisodes();
      break;
    case "episodes":
      if (state.allSeasons.length > 0) {
        state.view = "seasons";
        state.season = null;
        render();
      } else if (state.hits.length > 0) {
        showResults();
      } else {
        showHome();
      }
      break;
    case "seasons":
      if (state.hits.length > 0) showResults();
      else showHome();
      break;
    case "results":
      showHome();
      break;
    case "home":
      break;
  }
}

// --- démarrage ---------------------------------------------------------------

function applyTheme(theme: string): void {
  document.documentElement.dataset["theme"] = theme;
  ui.theme.textContent = theme === "light" ? "☀" : "☾";

  try {
    localStorage.setItem("aniweb-theme", theme);
  } catch {
    // stockage indisponible : le thème vaudra pour cette session
  }
}

ui.form.addEventListener("submit", (event) => {
  event.preventDefault();

  const query = ui.query.value.trim();
  if (query.length >= 2) void runSearch(query);
});

ui.query.addEventListener("input", scheduleSearch);

ui.source.addEventListener("change", () => {
  state.source = ui.source.value as Source;
  state.hits = [];

  showHome();
  scheduleSearch();
});

ui.lang.addEventListener("change", () => {
  const value = ui.lang.value;
  if (isLang(value)) void switchLang(value);
});

ui.home.addEventListener("click", () => showHome());

ui.theme.addEventListener("click", () => {
  applyTheme(document.documentElement.dataset["theme"] === "light" ? "dark" : "light");
});

ui.tv.addEventListener("click", () => {
  const on = !tv.isMode();

  tv.setMode(on);
  ui.tv.setAttribute("aria-pressed", String(on));
});

ui.settings.addEventListener("click", (event) => {
  // sans quoi le clic ressort et referme aussitôt ce qu'il vient d'ouvrir
  event.stopPropagation();
  setDrawer(ui.settingsPanel.hidden as boolean);
});

// un clic ailleurs referme ; un clic dans le tiroir ne compte pas
document.addEventListener("click", (event) => {
  if (ui.settingsPanel.hidden) return;
  if (ui.settingsPanel.contains(event.target as Node)) return;

  setDrawer(false);
});

ui.quality.addEventListener("change", () => {
  const quality = parseQuality(ui.quality.value);

  player.setQuality(quality);
  rememberQuality(quality);
  showQuality();
});

ui.preload.addEventListener("change", () => {
  const preload = parsePreload(ui.preload.value);

  player.setPreload(preload);

  try {
    localStorage.setItem(PRELOAD_KEY, preload);
  } catch {
    // stockage indisponible : le choix vaudra pour cette session seulement
  }
});

ui.prev.addEventListener("click", () => void play(state.current - 1));
ui.next.addEventListener("click", () => void play(state.current + 1));

ui.video.addEventListener("ended", () => {
  if (state.current < state.episodes.length - 1) void play(state.current + 1);
});

// --- raccourcis clavier du lecteur -------------------------------------------

function togglePlay(): void {
  if (ui.video.paused) void ui.video.play().catch(() => undefined);
  else ui.video.pause();
}

/** Durée d'un opening, la valeur qu'avance la touche S — celle du raccourci
 *  de ani-cli-fr. Le saut automatique, lui, utilise les intervalles d'AniSkip
 *  quand ils existent ; celui-ci marche partout, y compris sur les fiches
 *  qu'AniSkip ne connaît pas. */
const SKIP_SECONDS = 85;

function skipForward(): void {
  const { duration, currentTime } = ui.video;
  const target = currentTime + SKIP_SECONDS;

  // une durée inconnue (NaN) ou infinie ne doit pas borner le saut à rien
  ui.video.currentTime = Number.isFinite(duration)
    ? Math.min(target, Math.max(duration - 1, 0))
    : target;
}

/** Le plein écran porte sur le conteneur, pas sur la <video> : le bouton
 *  « Passer l'intro » se superpose au lecteur et doit rester visible. */
function toggleFullscreen(): void {
  if (document.fullscreenElement) {
    exitFullscreen();
    return;
  }

  const box = ui.video.parentElement ?? ui.video;

  void box.requestFullscreen().catch(() => undefined);
}

/** Sortie complète en cours : `exitFullscreen` ne dépile qu'un niveau. */
let leavingFullscreen = false;
/** Élément en plein écran avant le dernier changement. */
let fullscreenBefore: Element | null = null;

function exitFullscreen(): void {
  if (!document.fullscreenElement) return;

  leavingFullscreen = true;

  void document.exitFullscreen().catch(() => {
    leavingFullscreen = false;
  });
}

document.addEventListener("fullscreenchange", () => {
  const current = document.fullscreenElement;

  // Entré par F, le plein écran porte sur le conteneur. Le bouton du lecteur
  // natif ne regarde que la <video> : ne la voyant pas en plein écran, il la
  // y met à son tour, par-dessus le conteneur. Rien ne change à l'image, et
  // il fallait un second clic pour sortir. Ce niveau empilé ne peut venir que
  // de ce bouton, pressé pour sortir : on sort donc complètement.
  if (current === ui.video && fullscreenBefore === ui.video.parentElement) {
    leavingFullscreen = true;
  }

  fullscreenBefore = current;

  if (!leavingFullscreen) return;

  if (!current) {
    leavingFullscreen = false;
    return;
  }

  void document.exitFullscreen().catch(() => {
    leavingFullscreen = false;
  });
});

/* Mode développeur
   ----------------
   Deux choses : un relevé de la lecture en cours, rafraîchi à la seconde, et
   le test individuel des lecteurs proposés pour l'épisode. Tester un lecteur
   emprunte exactement le chemin de la lecture — résolution puis manifeste —
   mais ne joue rien : c'est ce qui permet de voir lequel répond quand un
   épisode refuse de démarrer. */

/** Périodique du relevé, 0 quand le panneau est fermé. */
let devTimer = 0;
/** Empêche deux campagnes de test simultanées. */
let devTesting = false;

function row(label: string, value: string): string {
  const safe = (text: string): string =>
    text.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);

  return `<dt>${safe(label)}</dt><dd>${safe(value)}</dd>`;
}

/** Débit lisible : les CDN annoncent des bits par seconde. */
function rate(bits: number): string {
  if (bits <= 0) return "—";
  if (bits >= 1_000_000) return `${(bits / 1_000_000).toFixed(1)} Mb/s`;

  return `${Math.round(bits / 1000)} kb/s`;
}

function renderDevStream(): void {
  const stream = state.stream;

  if (!stream) {
    ui.devStream.innerHTML = row("état", "aucun flux chargé");
    return;
  }

  const stats = player.stats();

  let host = stream.source;

  try {
    host = new URL(stream.source).hostname;
  } catch {
    // URL exotique : on garde la chaîne brute
  }

  const level =
    stats.height > 0
      ? `${stats.height}p${stats.bitrate > 0 ? ` · ${rate(stats.bitrate)}` : ""}` +
        (stats.auto ? " (auto)" : "")
      : "—";

  const frames =
    stats.frames > 0
      ? `${stats.dropped} / ${stats.frames}` +
        ` (${((stats.dropped / stats.frames) * 100).toFixed(1)} %)`
      : "—";

  ui.devStream.innerHTML = [
    row("lecteur", stream.player),
    row("hôte amont", host),
    row("type", stream.isHLS ? "HLS" : "progressif"),
    row("variantes", stats.levels > 0 ? String(stats.levels) : "—"),
    row("niveau", level),
    row("bande passante", rate(stats.bandwidth)),
    row("tampon", `${stats.buffer.toFixed(1)} s`),
    row("segments", `${stats.frags} chargés · ${stats.errors} erreur(s)`),
    row("dernier segment", stats.latency > 0 ? `${stats.latency} ms` : "—"),
    row("images perdues", frames),
  ].join("");
}

/** Compteurs servis par `/stats`. */
interface ProxyStats {
  requests: number;
  hits: number;
  upstream: number;
  errors: number;
  failures: { host: string; status: number; ago: number }[];
  blocked: number;
  uptime: number;
  cache: { entries: number; ttl: number };
}

/** Durée lisible : les compteurs courent depuis le démarrage du serveur. */
function duration(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;

  const hours = Math.floor(seconds / 3600);

  return `${hours} h ${Math.floor((seconds % 3600) / 60)}`;
}

async function renderDevServer(): Promise<void> {
  let stats: ProxyStats;

  try {
    const res = await fetch("/stats");
    if (!res.ok) throw new Error("indisponible");

    stats = (await res.json()) as ProxyStats;
  } catch {
    // serveur plus ancien, ou hors ligne
    ui.devServer.innerHTML = row("état", "compteurs indisponibles");
    ui.devFailures.replaceChildren();

    return;
  }

  // Un ratio n'a de sens qu'une fois quelques requêtes passées.
  const share =
    stats.requests > 0 ? ` (${Math.round((stats.hits / stats.requests) * 100)} %)` : "";

  ui.devServer.innerHTML = [
    row("depuis", duration(stats.uptime)),
    row("requêtes", String(stats.requests)),
    row("servies du cache", `${stats.hits}${share}`),
    row(
      "cache",
      stats.cache.ttl > 0
        ? `${stats.cache.entries} entrée(s) · ${stats.cache.ttl} s`
        : "désactivé",
    ),
    row("parties en amont", String(stats.upstream)),
    row("erreurs amont", String(stats.errors)),
    row("hôtes refusés", String(stats.blocked)),
  ].join("");

  ui.devFailures.replaceChildren(
    ...stats.failures
      .slice(0, 5)
      .map((failure) =>
        devInfo(
          `${failure.host} · ${failure.status === 0 ? "injoignable" : failure.status}` +
            ` · il y a ${duration(failure.ago)}`,
        ),
      ),
  );
}

/** Une ligne de la liste des lecteurs. `probe` absent = test en cours. */
function devPlayerRow(player: string, probe: Probe | null): HTMLElement {
  const item = document.createElement("li");

  if (probe) item.className = probe.ok ? "ok" : "ko";

  const mark = document.createElement("span");
  mark.className = "dev-mark";
  mark.textContent = probe ? (probe.ok ? "✓" : "✗") : "…";

  const name = document.createElement("span");
  name.textContent = player;

  const ms = document.createElement("span");
  ms.className = "dev-ms";
  ms.textContent = probe ? `${probe.ms} ms` : "";

  const detail = document.createElement("span");
  detail.className = "dev-detail";

  if (probe) {
    detail.textContent = probe.host ? `${probe.detail} · ${probe.host}` : probe.detail;
  } else {
    detail.textContent = "test en cours…";
  }

  item.append(mark, name, ms, detail);

  return item;
}

async function runDevTests(): Promise<void> {
  if (devTesting) return;

  const embeds = await embedsFor(state.current);

  if (embeds.length === 0) {
    ui.devPlayers.replaceChildren(devInfo("aucun lecteur pour cet épisode"));
    return;
  }

  devTesting = true;
  ui.devTest.disabled = true;

  // toutes les lignes sont posées d'emblée, puis remplies à mesure : l'ordre
  // est celui d'essai réel, celui qu'emprunterait la lecture
  const rows = embeds.map((embed) => devPlayerRow(embedLabel(embed), null));
  ui.devPlayers.replaceChildren(...rows);

  try {
    await probeEmbeds(embeds, (probe, index) => {
      rows[index]!.replaceWith((rows[index] = devPlayerRow(probe.player, probe)));
    });
  } finally {
    devTesting = false;
    ui.devTest.disabled = false;
  }
}

/** Ligne de texte libre : une note, ou un échec amont. */
function devInfo(message: string): HTMLElement {
  const item = document.createElement("li");

  item.className = "dev-note";
  item.textContent = message;

  return item;
}

function setDev(on: boolean): void {
  ui.devPanel.hidden = !on;
  localStorage.setItem("aniweb-dev", on ? "1" : "0");

  window.clearInterval(devTimer);
  devTimer = 0;

  if (!on) return;

  const refresh = (): void => {
    renderDevStream();
    void renderDevServer();
  };

  refresh();
  devTimer = window.setInterval(refresh, 1000);

  // la liste des lecteurs n'est pas retestée d'office : chaque test est une
  // requête vers la source, elle part sur demande
  if (ui.devPlayers.childElementCount === 0) {
    ui.devPlayers.replaceChildren(devInfo("« Tout tester » pour sonder les lecteurs"));
  }
}

ui.devClose.addEventListener("click", () => setDev(false));
ui.devTest.addEventListener("click", () => void runDevTests());

document.addEventListener("keydown", (event) => {
  // F8 bascule le panneau partout, y compris depuis un champ de saisie : ce
  // n'est pas une touche de frappe, rien ne la lui dispute.
  if (event.key === "F8" && !event.altKey && !event.ctrlKey && !event.metaKey) {
    event.preventDefault();
    setDev(ui.devPanel.hidden as boolean);

    return;
  }

  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (state.view !== "player") return;

  // jamais voler une frappe à un champ de saisie ou à une liste déroulante
  const tag = (document.activeElement as HTMLElement | null)?.tagName ?? "";
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

  switch (event.key.toLowerCase()) {
    case "f":
      event.preventDefault();
      toggleFullscreen();
      break;

    case "k":
      event.preventDefault();
      togglePlay();
      break;

    case "s":
      event.preventDefault();
      skipForward();
      break;
  }
});

ui.downloadBtn.addEventListener("click", () => void startDownload());
ui.downloadCancel.addEventListener("click", () => downloadAbort?.abort());

tv.install({ onBack: goBack });
tv.restoreMode();
ui.tv.setAttribute("aria-pressed", String(tv.isMode()));

applyTheme(localStorage.getItem("aniweb-theme") ?? "dark");
ui.autoskip.checked = localStorage.getItem("aniweb-autoskip") === "1";
setDev(localStorage.getItem("aniweb-dev") === "1");
player.setQuality(storedQuality());

ui.preload.value = storedPreload();
player.setPreload(storedPreload());

// Les domaines des sources viennent du serveur : rien ne doit partir vers l'une
// d'elles avant sa réponse. C'est une requête locale, et un échec n'empêche pas
// l'interface de s'afficher.
await loadConfig();

showHome();
if (!tv.isMode()) ui.query.focus();
